import type { TFunction } from "i18next";
import { ADVISOR_DEFAULT_SLUG } from "./advisor.data";
import type { IAdvisorStarter } from "./types";
import {
  Banknote,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  Coins,
  FileText,
  Landmark,
  MessagesSquare,
  Plane,
  Receipt,
  Scale,
  ScrollText,
  Target,
  UserPlus,
  UsersRound,
  Wallet,
} from "lucide-react";

/**
 * Starter questions, keyed by advisor — the Workspace counterpart to
 * `my-space-starters.data.ts`. Shared by the Workspace AI Assistant page and
 * the Maya sidebar on Workspace pages. Suggestions that don't match the
 * selected advisor are worse than no suggestions — a labour-law advisor
 * offering "who's on leave today" teaches the user the picker does nothing.
 *
 * `prompt` is the fully-formed query sent to Maya; `title`/`description` are
 * what the user reads. All three are translated — `prompt` is also what gets
 * sent as the user's own chat message, so it should read in the UI language.
 */
const buildStarters = (t: TFunction): Record<string, IAdvisorStarter[]> => ({
  [ADVISOR_DEFAULT_SLUG]: [
    {
      icon: Plane,
      title: t(
        "ai-assistant:starters.company.default.who_on_leave_today.title",
      ),
      description: t(
        "ai-assistant:starters.company.default.who_on_leave_today.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.default.who_on_leave_today.prompt",
      ),
    },
    {
      icon: UserPlus,
      title: t(
        "ai-assistant:starters.company.default.new_joiners_this_month.title",
      ),
      description: t(
        "ai-assistant:starters.company.default.new_joiners_this_month.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.default.new_joiners_this_month.prompt",
      ),
    },
    {
      icon: Wallet,
      title: t(
        "ai-assistant:starters.company.default.this_months_payroll.title",
      ),
      description: t(
        "ai-assistant:starters.company.default.this_months_payroll.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.default.this_months_payroll.prompt",
      ),
    },
    {
      icon: FileText,
      title: t(
        "ai-assistant:starters.company.default.expiring_documents.title",
      ),
      description: t(
        "ai-assistant:starters.company.default.expiring_documents.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.default.expiring_documents.prompt",
      ),
    },
  ],
  "uae-labour-law": [
    {
      icon: Coins,
      title: t(
        "ai-assistant:starters.company.uae_labour_law.end_of_service_calculation.title",
      ),
      description: t(
        "ai-assistant:starters.company.uae_labour_law.end_of_service_calculation.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.uae_labour_law.end_of_service_calculation.prompt",
      ),
    },
    {
      icon: CalendarClock,
      title: t(
        "ai-assistant:starters.company.uae_labour_law.notice_periods_by_exit_type.title",
      ),
      description: t(
        "ai-assistant:starters.company.uae_labour_law.notice_periods_by_exit_type.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.uae_labour_law.notice_periods_by_exit_type.prompt",
      ),
    },
    {
      icon: Scale,
      title: t(
        "ai-assistant:starters.company.uae_labour_law.check_policy_against_law.title",
      ),
      description: t(
        "ai-assistant:starters.company.uae_labour_law.check_policy_against_law.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.uae_labour_law.check_policy_against_law.prompt",
      ),
    },
    {
      icon: CalendarDays,
      title: t(
        "ai-assistant:starters.company.uae_labour_law.leave_entitlements.title",
      ),
      description: t(
        "ai-assistant:starters.company.uae_labour_law.leave_entitlements.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.uae_labour_law.leave_entitlements.prompt",
      ),
    },
  ],
  "hr-advisor": [
    {
      icon: Target,
      title: t(
        "ai-assistant:starters.company.hr_advisor.design_probation_review.title",
      ),
      description: t(
        "ai-assistant:starters.company.hr_advisor.design_probation_review.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.hr_advisor.design_probation_review.prompt",
      ),
    },
    {
      icon: MessagesSquare,
      title: t(
        "ai-assistant:starters.company.hr_advisor.script_difficult_conversation.title",
      ),
      description: t(
        "ai-assistant:starters.company.hr_advisor.script_difficult_conversation.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.hr_advisor.script_difficult_conversation.prompt",
      ),
    },
    {
      icon: UsersRound,
      title: t(
        "ai-assistant:starters.company.hr_advisor.reduce_turnover.title",
      ),
      description: t(
        "ai-assistant:starters.company.hr_advisor.reduce_turnover.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.hr_advisor.reduce_turnover.prompt",
      ),
    },
    {
      icon: ScrollText,
      title: t("ai-assistant:starters.company.hr_advisor.draft_a_policy.title"),
      description: t(
        "ai-assistant:starters.company.hr_advisor.draft_a_policy.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.hr_advisor.draft_a_policy.prompt",
      ),
    },
  ],
  "payroll-tax": [
    {
      icon: Landmark,
      title: t(
        "ai-assistant:starters.company.payroll_tax.fix_rejected_wps_file.title",
      ),
      description: t(
        "ai-assistant:starters.company.payroll_tax.fix_rejected_wps_file.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.payroll_tax.fix_rejected_wps_file.prompt",
      ),
    },
    {
      icon: Receipt,
      title: t(
        "ai-assistant:starters.company.payroll_tax.corporate_tax_on_staff_costs.title",
      ),
      description: t(
        "ai-assistant:starters.company.payroll_tax.corporate_tax_on_staff_costs.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.payroll_tax.corporate_tax_on_staff_costs.prompt",
      ),
    },
    {
      icon: Banknote,
      title: t(
        "ai-assistant:starters.company.payroll_tax.pension_contributions.title",
      ),
      description: t(
        "ai-assistant:starters.company.payroll_tax.pension_contributions.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.payroll_tax.pension_contributions.prompt",
      ),
    },
    {
      icon: ClipboardCheck,
      title: t(
        "ai-assistant:starters.company.payroll_tax.payroll_cutoff_calendar.title",
      ),
      description: t(
        "ai-assistant:starters.company.payroll_tax.payroll_cutoff_calendar.description",
      ),
      prompt: t(
        "ai-assistant:starters.company.payroll_tax.payroll_cutoff_calendar.prompt",
      ),
    },
  ],
});

export const advisorStarters = (
  slug: string,
  t: TFunction,
): IAdvisorStarter[] => {
  const starters = buildStarters(t);
  return starters[slug] ?? starters[ADVISOR_DEFAULT_SLUG];
};
